import { useCallback, useEffect, useId, useRef, useState } from 'react';

const CLOSE_DELAY_MS = 220;

/** One header menu open at a time; pinned menus are left alone. */
const openMenus = new Map<string, () => void>();

function closeOtherMenus(exceptId: string) {
  for (const [id, close] of openMenus) {
    if (id !== exceptId) close();
  }
}

/** True when the pointer is moving into one of the given containers (trigger / menu). */
export function isHoverMovingWithin(relatedTarget: EventTarget | null, containers: Array<Element | null | undefined>) {
  if (!(relatedTarget instanceof Node)) return false;
  return containers.some((el) => el?.contains(relatedTarget));
}

interface UseHoverMenuOptions {
  /** Fired once, on first open. */
  onPrefetch?: () => void;
  /** Ignore hover-out and sibling opens; only close/escape/toggle dismiss the menu. */
  pinned?: boolean;
}

/**
 * Hover-intent state for a header dropdown: opens on enter, closes shortly after leave,
 * click toggles for touch/keyboard. `mounted` flips on first open and stays true for lazy mounting.
 */
export function useHoverMenu({ onPrefetch, pinned = false }: UseHoverMenuOptions = {}) {
  const menuId = useId();
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prefetched = useRef(false);
  const openedByHover = useRef(false);
  const pinnedRef = useRef(pinned);
  pinnedRef.current = pinned;

  const cancelClose = useCallback(() => {
    if (closeTimer.current !== null) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  const handleClose = useCallback(() => {
    cancelClose();
    openedByHover.current = false;
    setOpen(false);
  }, [cancelClose]);

  useEffect(() => {
    openMenus.set(menuId, () => {
      if (!pinnedRef.current) handleClose();
    });
    return () => {
      openMenus.delete(menuId);
      cancelClose();
    };
  }, [menuId, handleClose, cancelClose]);

  const openMenu = useCallback(() => {
    cancelClose();
    closeOtherMenus(menuId);
    if (!prefetched.current) {
      prefetched.current = true;
      onPrefetch?.();
    }
    setMounted(true);
    setOpen(true);
  }, [cancelClose, menuId, onPrefetch]);

  const handleEnter = useCallback(() => {
    openedByHover.current = true;
    openMenu();
  }, [openMenu]);

  const handleLeave = useCallback(() => {
    if (pinnedRef.current) return;
    cancelClose();
    closeTimer.current = setTimeout(() => {
      openedByHover.current = false;
      setOpen(false);
    }, CLOSE_DELAY_MS);
  }, [cancelClose]);

  // A click closes only a click-opened menu, so hover-then-click doesn't make it vanish.
  const handleToggle = useCallback(() => {
    cancelClose();
    if (open && !openedByHover.current) {
      setOpen(false);
      return;
    }
    openedByHover.current = false;
    openMenu();
  }, [cancelClose, open, openMenu]);

  const handleEscape = useCallback(
    (triggerEl?: HTMLElement | null) => {
      handleClose();
      triggerEl?.focus();
    },
    [handleClose]
  );

  return { open, mounted, menuId, handleEnter, handleLeave, handleToggle, handleClose, handleEscape };
}
